import React, { useState } from "react";
import { get } from "lodash";
import {
  RaceProgram,
  RaceTypeCodeEnum,
  RaceWagerType
} from "@tvg/ts-types/Race";
import { useDispatch, useSelector } from "react-redux";
import { Header, Saddle, useTheme } from "@tvg/design-system";
import {
  allLegRunnersSelected,
  buildRacesMatrix,
  getRaceSelectedRace,
  getRaceWithMoreColumns,
  isBiScratched
} from "@tvg/desktop-bet/src/utils/betUtils";
// Store
import { setPickLeg } from "@tvg/desktop-bet/src/store/actions";
import { getBetLeg } from "@tvg/desktop-bet/src/store/selectors";
import {
  PickSelectorHeader,
  PickSelectorMainContainer,
  PicksSelectorColumnContainer,
  PicksSelectorContainer,
  PicksSelectorEntryContainer,
  PicksSelectorEntryLabelContainer,
  PicksSelectorHeaderEntryContainer,
  PicksSelectorRunnerInfoColumnContainer,
  PicksSelectorSaddleContainer
} from "./styled-components";
import PicksSelectorCheck from "./PickSelectorCheck/index";
import PicksSelectorCellEventsContainer from "./PicksSelectorCellEvents/index";
import CurrentRaceDetails from "../Main/CurrentRaceDetails";
import PickSelectorRunnerEntry from "./PickSelectorRunnerEntry";
import ScrollablePicksRacecard from "../Main/ScrollablePicksRacecard";
import { PickSelectRunnerEvent } from "../../utils/gtm/selection";
import {
  PickViewOptionsEnum,
  RunnerModifierEnum,
  RunnerSortByEnum,
  RunnerSortOrderEnum
} from "../../types";
import { getShowHandicappingOptionPicksToggle } from "../../store/selectors";
import { RunnerBI } from "../../hooks/useGetRunnerFromBI";
import PicksModifiers from "./PicksModifiers";
import { FreePastPerformanceData } from "../Main/ScrollablePicksRacecard/types";

interface PicksSelectorProps {
  isMobile?: boolean;
  isBetSlipVisible?: boolean;
  runnerModifier?: RunnerModifierEnum;
  runnerSortOrder?: RunnerSortOrderEnum;
  runnerSortBy?: RunnerSortByEnum;
  renderRunnerModifier?: (
    currentRace: RaceProgram,
    isPicks?: boolean,
    legIndex?: number
  ) => JSX.Element;
  currentRace: RaceProgram;
  races: RaceProgram[];
  selectedBetType?: RaceWagerType;
  selections: string[][];
  onClickBetButton: (
    runner: string | RunnerBI,
    type: string,
    position?: number,
    module?: string
  ) => void;
  onClickActiveBetsButton?: () => void;
  freePastPerformanceData?: FreePastPerformanceData;
}

const PicksSelector = ({
  isMobile,
  isBetSlipVisible,
  runnerModifier,
  runnerSortOrder,
  runnerSortBy,
  renderRunnerModifier,
  currentRace,
  races,
  selectedBetType,
  selections,
  onClickBetButton,
  onClickActiveBetsButton,
  freePastPerformanceData
}: PicksSelectorProps) => {
  const [coordinates, setCoordinates] = useState<{
    line: number | null;
    column: number | null;
  }>({ line: null, column: 0 });
  const theme = useTheme();
  const dispatch = useDispatch();

  const [pickView, setPickView] = useState<PickViewOptionsEnum>(
    PickViewOptionsEnum.QUICK_PICK
  );

  const selectedBetLeg = useSelector(getBetLeg);
  const hasAllRaces = !races.some(
    (elem) => elem.bettingInterests === undefined
  );

  const showPickModifierContainer = useSelector(
    getShowHandicappingOptionPicksToggle
  );

  const onSelectedCell = (line: number | null, column: number | null) => {
    setCoordinates({
      line,
      column
    });
  };

  const matrix = buildRacesMatrix(
    currentRace.id,
    races,
    selectedBetType?.legCount || 0
  );

  const selectedRace = getRaceSelectedRace(matrix, coordinates.column);
  const selectedRaceBi = selectedRace?.bettingInterests || [];
  const biggestRaceBI = getRaceWithMoreColumns(matrix)?.bettingInterests || [];

  const onSelectRunner = (
    biNumber: string,
    line: number,
    column: number,
    isSelected: boolean
  ) => {
    onSelectedCell(line, column);
    if (isSelected) {
      PickSelectRunnerEvent({
        race: selectedRace,
        betType: selectedBetType?.type.name || ""
      });
    }
    onClickBetButton(biNumber, selectedBetType?.type.code || "", column);
  };

  const onSelectAll = (column: number, isSelected: boolean) => {
    onSelectedCell(null, column);
    if (isSelected) {
      PickSelectRunnerEvent({
        race: selectedRace,
        betType: selectedBetType?.type.name || "",
        column
      });
    }
    onClickBetButton("all", selectedBetType?.type.code || "", column);
  };

  const onSelectBetLeg = (leg: number) => dispatch(setPickLeg(leg));

  return (
    <PicksSelectorContainer>
      {showPickModifierContainer && !isMobile && (
        <PicksModifiers
          activeView={pickView}
          currentRace={currentRace}
          onChangeView={setPickView}
          renderRunnerModifier={renderRunnerModifier}
        />
      )}
      {(pickView === PickViewOptionsEnum.HANDICAPPING || isMobile) && (
        <ScrollablePicksRacecard
          key={`scrollable-race-card-${currentRace.id}`}
          selectedBetType={selectedBetType}
          selectedBetLeg={selectedBetLeg}
          runnerSortBy={runnerSortBy}
          runnerSortOrder={runnerSortOrder}
          runnerModifier={runnerModifier}
          renderRunnerModifier={renderRunnerModifier}
          isBetSlipVisible={isBetSlipVisible}
          isMobile={isMobile}
          races={matrix}
          selections={selections}
          onClickBetButton={onClickBetButton}
          onSelectBetLeg={onSelectBetLeg}
          onClickActiveBetsButton={onClickActiveBetsButton}
          freePastPerformanceData={freePastPerformanceData}
        />
      )}
      {pickView === PickViewOptionsEnum.QUICK_PICK && !isMobile && (
        <>
          <PickSelectorHeader theme={theme}>
            <PicksSelectorHeaderEntryContainer
              isSelected={false}
              theme={theme}
            />
            {matrix.map((raceColumn, columnIndex) => (
              <PicksSelectorHeaderEntryContainer
                isSelected={false}
                theme={theme}
                key={`PickRunnerHeader-${columnIndex.toString()}`}
              >
                <PicksSelectorCellEventsContainer
                  columnNumber={columnIndex}
                  lineNumber={null}
                  selectedLine={coordinates.line}
                  selectedColumn={coordinates.column}
                  disabled={false}
                  onSelectedCell={onSelectedCell}
                  top
                  hasNextRaces={hasAllRaces}
                  isHeader
                >
                  <>
                    <PicksSelectorEntryLabelContainer>
                      <Header
                        fontFamily="medium"
                        fontSize="m"
                        tag="h3"
                        color="grey.800"
                        qaLabel="runnerName"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {`L${columnIndex + 1}`}
                      </Header>
                    </PicksSelectorEntryLabelContainer>
                    <PicksSelectorCheck
                      columnNumber={columnIndex}
                      lineNumber={null}
                      selectedValues={allLegRunnersSelected(
                        columnIndex,
                        raceColumn,
                        selections
                      )}
                      race={raceColumn}
                      onSelectAll={onSelectAll}
                      hasAllRaces={hasAllRaces || columnIndex === 0}
                    />
                  </>
                </PicksSelectorCellEventsContainer>
              </PicksSelectorHeaderEntryContainer>
            ))}
            <CurrentRaceDetails
              currentRace={selectedRace || currentRace}
              isPickHeader
              mainCurrentRace={currentRace}
            />
          </PickSelectorHeader>
          <PickSelectorMainContainer>
            <PicksSelectorColumnContainer data-qa-label="picks-selector-column-container">
              {biggestRaceBI.map((_bi, index) => (
                <PicksSelectorEntryContainer
                  theme={theme}
                  key={`PickRunnerSaddle-${index.toString()}`}
                >
                  <PicksSelectorCellEventsContainer
                    columnNumber={null}
                    lineNumber={index}
                    selectedLine={coordinates.line}
                    selectedColumn={coordinates.column}
                    onSelectedCell={() => {
                      setCoordinates({
                        line: index,
                        column: coordinates.column
                      });
                    }}
                    disabled={false}
                    side
                    hasNextRaces={true}
                  >
                    <PicksSelectorSaddleContainer>
                      {selectedRaceBi[index]?.runners[0] && (
                        <Saddle
                          isScratched={isBiScratched(selectedRaceBi[index])}
                          number={selectedRaceBi[index].biNumber}
                          numberColor={selectedRaceBi[index].numberColor}
                          saddleColor={selectedRaceBi[index].saddleColor}
                          size="s"
                        />
                      )}
                    </PicksSelectorSaddleContainer>
                  </PicksSelectorCellEventsContainer>
                </PicksSelectorEntryContainer>
              ))}
            </PicksSelectorColumnContainer>
            {matrix.map((raceColumn, columnIndex) => (
              <PicksSelectorColumnContainer
                data-qa-label={`picks-selector-leg-container-${
                  columnIndex + 1
                }`}
                key={`PickRunnerMatrix-column-${columnIndex.toString()}`}
              >
                {biggestRaceBI.map((_bi, lineIndex) => (
                  <PicksSelectorEntryContainer
                    theme={theme}
                    key={`PickRunnerMatrix-line-${lineIndex.toString()}`}
                  >
                    <PicksSelectorCellEventsContainer
                      columnNumber={columnIndex}
                      lineNumber={lineIndex}
                      selectedLine={coordinates.line}
                      selectedColumn={coordinates.column}
                      onSelectedCell={onSelectedCell}
                      isMatrix
                      hasNextRaces={hasAllRaces}
                      disabled={
                        hasAllRaces
                          ? isBiScratched(
                              get(raceColumn, `bettingInterests[${lineIndex}]`)
                            ) !== false
                          : false
                      }
                    >
                      <PicksSelectorCheck
                        columnNumber={columnIndex}
                        lineNumber={lineIndex}
                        biNumber={`${get(
                          raceColumn,
                          `bettingInterests[${lineIndex}].biNumber`,
                          null
                        )}`}
                        selectedValues={selections[columnIndex] || []}
                        onSelectRunner={onSelectRunner}
                        hasAllRaces={hasAllRaces || columnIndex === 0}
                      />
                    </PicksSelectorCellEventsContainer>
                  </PicksSelectorEntryContainer>
                ))}
              </PicksSelectorColumnContainer>
            ))}
            <PicksSelectorRunnerInfoColumnContainer data-qa-label="picks-selector-runner-info-column">
              {biggestRaceBI.map((_bi, index) => (
                <PicksSelectorCellEventsContainer
                  key={`PickRunnerInfo-${
                    coordinates.column
                  }-${index.toString()}`}
                  columnNumber={null}
                  lineNumber={index}
                  selectedLine={coordinates.line}
                  selectedColumn={coordinates.column}
                  onSelectedCell={() => {
                    setCoordinates({ line: index, column: coordinates.column });
                  }}
                  disabled={false}
                  hasNextRaces={true}
                >
                  <PickSelectorRunnerEntry
                    bi={selectedRaceBi[index]}
                    isDriver={
                      selectedRace?.type?.code === RaceTypeCodeEnum.HARNESS
                    }
                    raceChanges={selectedRace.changes}
                  />
                </PicksSelectorCellEventsContainer>
              ))}
            </PicksSelectorRunnerInfoColumnContainer>
          </PickSelectorMainContainer>
        </>
      )}
    </PicksSelectorContainer>
  );
};

export default PicksSelector;
